import { useState } from 'react';
import useLang from '../../hooks/useLang';
import AdamKhooDCF from './AdamKhooDCF';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, BarChart, Bar, Cell, LabelList,
  AreaChart
} from 'recharts';
import { fmt, colorByValue } from '../../utils/format';
import { getValuation } from '../../utils/api';

/* ── Horizontal Valuation Bar Chart ── */
function ValuationBars({ models, currentPrice }) {
  if (!models.length) return null;
  const max = Math.max(...models.map(m => m.value), currentPrice) * 1.2;

  return (
    <div>
      <div className="card-title">Valuation Methods vs Current Price</div>
      <div style={{ position: 'relative' }}>
        {/* Current price dashed line overlay */}
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${(currentPrice / max) * 100}%`, width: 2, background: 'var(--accent)', zIndex: 10, opacity: 0.8 }} />
        <div style={{ position: 'absolute', top: -18, left: `${(currentPrice / max) * 100}%`, transform: 'translateX(-50%)', fontSize: 10, color: 'var(--accent)', fontFamily: 'JetBrains Mono', whiteSpace: 'nowrap', zIndex: 11 }}>
          Price: {fmt.currency(currentPrice)}
        </div>
        <div style={{ marginTop: 8 }}>
          {models.map((m) => {
            const pct = (m.value / max) * 100;
            const discount = ((m.value - currentPrice) / currentPrice) * 100;
            const color = discount > 20 ? '#22c55e' : discount > 0 ? '#86efac' : discount > -20 ? '#fbbf24' : '#ef4444';
            return (
              <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ width: 160, textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0 }}>{m.label}</div>
                <div style={{ flex: 1, position: 'relative', height: 28 }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'var(--bg-surface)', borderRadius: 4 }} />
                  <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${pct}%`, background: color, borderRadius: 4, opacity: 0.85, transition: 'width 0.5s' }} />
                  <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: `${pct + 1}%`, fontSize: 12, fontFamily: 'JetBrains Mono', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                    {fmt.currency(m.value)}
                    <span style={{ marginLeft: 6, fontSize: 10, color }}>{discount >= 0 ? '+' : ''}{discount.toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ── Price vs Fair Value Band Chart ── */
function PriceBandChart({ priceHistory, fairValue }) {
  if (!priceHistory?.length || !fairValue) return null;

  const data = [...priceHistory].reverse().map(p => {
    const fv = fairValue;
    return {
      date: p.date?.slice(5),
      price: p.close,
      fv,
      band30up: +(fv * 1.30).toFixed(2),
      band10up: +(fv * 1.10).toFixed(2),
      band10dn: +(fv * 0.90).toFixed(2),
      band30dn: +(fv * 0.70).toFixed(2),
    };
  });

  const allValues = data.flatMap(d => [d.price, d.band30up, d.band30dn]).filter(Boolean);
  const yMin = Math.floor(Math.min(...allValues) * 0.95);
  const yMax = Math.ceil(Math.max(...allValues) * 1.05);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const price = payload.find(p => p.dataKey === 'price')?.value;
    const fv = payload.find(p => p.dataKey === 'fv')?.value;
    const disc = fv ? (((price - fv) / fv) * 100).toFixed(1) : null;
    return (
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, padding: '10px 14px', fontSize: 12 }}>
        <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
        <div style={{ color: 'var(--text-primary)' }}>Price: <span style={{ fontFamily: 'JetBrains Mono' }}>{fmt.currency(price)}</span></div>
        <div style={{ color: 'var(--accent)' }}>Fair Value: <span style={{ fontFamily: 'JetBrains Mono' }}>{fmt.currency(fv)}</span></div>
        {disc && <div style={{ color: parseFloat(disc) > 0 ? 'var(--red)' : 'var(--green)' }}>vs FV: {disc > 0 ? '+' : ''}{disc}%</div>}
      </div>
    );
  };

  return (
    <div>
      <div className="card-title">Price vs Fair Value</div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, fontSize: 11 }}>
        {[['Significantly Overvalued (+30%)', '#ef444440'], ['Overvalued (+10%)', '#f9731640'], ['Fair Value Zone', '#22c55e30'], ['Undervalued (-30%)', '#22c55e50']].map(([label, color]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 12, height: 12, background: color, borderRadius: 2 }} />
            <span style={{ color: 'var(--text-muted)' }}>{label}</span>
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} interval={Math.floor(data.length / 6)} />
          <YAxis domain={[yMin, yMax]} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickFormatter={v => `$${v}`} />
          <Tooltip content={<CustomTooltip />} />

          {/* +30% to +10% — significantly overvalued (red) */}
          <Area type="monotone" dataKey="band30up" stroke="none" fill="#ef444430" fillOpacity={1} legendType="none" />
          <Area type="monotone" dataKey="band10up" stroke="none" fill="#ef444430" fillOpacity={1} legendType="none" />

          {/* +10% to -10% — fair zone (green) */}
          <Area type="monotone" dataKey="band10up" stroke="none" fill="#22c55e25" fillOpacity={1} legendType="none" />
          <Area type="monotone" dataKey="band10dn" stroke="none" fill="#22c55e25" fillOpacity={1} legendType="none" />

          {/* -10% to -30% — undervalued (green) */}
          <Area type="monotone" dataKey="band10dn" stroke="none" fill="#22c55e35" fillOpacity={1} legendType="none" />
          <Area type="monotone" dataKey="band30dn" stroke="none" fill="#22c55e35" fillOpacity={1} legendType="none" />

          {/* Fair value line */}
          <Line type="monotone" dataKey="fv" stroke="var(--accent)" strokeWidth={2} dot={false} strokeDasharray="6 3" name="Fair Value" />

          {/* Price line */}
          <Line type="monotone" dataKey="price" stroke="var(--text-primary)" strokeWidth={2.5} dot={false} name="Price" />

          {/* Band labels */}
          <ReferenceLine y={fairValue * 1.30} stroke="#ef4444" strokeDasharray="3 3" strokeOpacity={0.5} label={{ value: '+30%', fill: '#ef4444', fontSize: 10, position: 'right' }} />
          <ReferenceLine y={fairValue * 1.10} stroke="#f97316" strokeDasharray="3 3" strokeOpacity={0.5} label={{ value: '+10%', fill: '#f97316', fontSize: 10, position: 'right' }} />
          <ReferenceLine y={fairValue * 0.90} stroke="#86efac" strokeDasharray="3 3" strokeOpacity={0.5} label={{ value: '-10%', fill: '#86efac', fontSize: 10, position: 'right' }} />
          <ReferenceLine y={fairValue * 0.70} stroke="#22c55e" strokeDasharray="3 3" strokeOpacity={0.5} label={{ value: '-30%', fill: '#22c55e', fontSize: 10, position: 'right' }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function ValuationPanel({ valuation, symbol, technicals }) {
  const { lang } = useLang();
  const isChinese = lang === 'zh';
  const [assumptions, setAssumptions] = useState({ discountRate: 10, terminalGrowth: 2.5, growthRate: 10 });
  const [data, setData] = useState(valuation);
  const [loading, setLoading] = useState(false);

  const recalc = async () => {
    setLoading(true);
    try {
      const result = await getValuation(symbol, {
        discountRate: assumptions.discountRate / 100,
        terminalGrowth: assumptions.terminalGrowth / 100,
        growthRate: assumptions.growthRate / 100
      });
      setData(result);
    } finally { setLoading(false); }
  };

  const recalcAdamKhoo = async (adamKhooAssumptions) => {
    const result = await getValuation(symbol, {
      discountRate: assumptions.discountRate / 100,
      terminalGrowth: assumptions.terminalGrowth / 100,
      growthRate: assumptions.growthRate / 100,
      adamKhoo: adamKhooAssumptions
    });
    setData(result);
  };

  if (!data) return null;
  const { dcf, pe, graham, fcfYield, pb, isFinancialSector, compositeFairValue, currentPrice, verdict } = data;
  const forwardValuation = data.rawData?.forwardValuation || null;

  const verdictColor = verdict === 'Undervalued' ? 'var(--green)'
    : verdict === 'Overvalued' || verdict === 'Slightly Overvalued' ? 'var(--red)'
    : 'var(--yellow)';

  const models = [
    dcf?.intrinsicValue ? { label: 'DCF (FCF Based)', value: dcf.intrinsicValue } : null,
    pe?.intrinsicValue ? { label: 'P/E Relative', value: pe.intrinsicValue } : null,
    graham?.intrinsicValue ? { label: 'Graham Number', value: graham.intrinsicValue } : null,
    pb?.intrinsicValue ? { label: 'P/B Valuation', value: pb.intrinsicValue } : null,
    compositeFairValue ? { label: 'Composite Fair Value', value: compositeFairValue } : null,
  ].filter(Boolean);

  const fcfChartData = (data.rawData?.fcfHistory || []).map(d => ({ year: d.date?.slice(0, 4), FCF: d.fcf ? d.fcf / 1e9 : null })).reverse();
  const priceHistory = technicals?.priceHistory || [];

  return (
    <div>
      {/* Summary Header */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap', marginBottom: 20 }}>
          <div>
            <div className="stat-label">Current Price</div>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 26, fontWeight: 600 }}>{fmt.currency(currentPrice)}</div>
          </div>
          <div>
            <div className="stat-label">Fair Value Estimate</div>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: 26, fontWeight: 600, color: 'var(--accent)' }}>{fmt.currency(compositeFairValue)}</div>
          </div>
          <div>
            <div className="stat-label">Verdict</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: verdictColor }}>{verdict}</div>
          </div>
          {compositeFairValue && currentPrice && (
            <div>
              <div className="stat-label">Margin of Safety</div>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: 20, fontWeight: 600, color: verdictColor }}>
                {(((compositeFairValue - currentPrice) / compositeFairValue) * 100).toFixed(1)}%
              </div>
            </div>
          )}
        </div>
        {isFinancialSector && (
          <div style={{ fontSize: 11, color: 'var(--accent)', background: 'var(--accent-dim)', borderRadius: 4, padding: '4px 10px', marginBottom: 14, display: 'inline-block' }}>
            Financial sector — using P/B + P/E valuation (DCF excluded)
          </div>
        )}
        <div className="grid-4">
          {dcf && <div className="stat-cell"><div className="stat-label">DCF Value</div><div className="stat-value">{fmt.currency(dcf.intrinsicValue)}</div></div>}
          {pe && <div className="stat-cell"><div className="stat-label">P/E Relative</div><div className="stat-value">{fmt.currency(pe.intrinsicValue)}</div></div>}
          {graham && <div className="stat-cell"><div className="stat-label">Graham Number</div><div className="stat-value">{fmt.currency(graham.intrinsicValue)}</div></div>}
          {pb && (
            <div className="stat-cell">
              <div className="stat-label">P/B Valuation</div>
              <div className="stat-value">{fmt.currency(pb.intrinsicValue)}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                BVPS {fmt.currency(pb.bvps)} × {pb.targetPB}x
                {pb.roe != null ? ` | ROE ${pb.roe}%` : ''}
              </div>
            </div>
          )}
          {fcfYield && <div className="stat-cell"><div className="stat-label">FCF Yield</div><div className="stat-value" style={{ color: fcfYield.isAttractive ? 'var(--green)' : 'var(--text-primary)' }}>{fcfYield.yieldPct != null ? `${fcfYield.yieldPct}%` : '—'}</div></div>}
        </div>
      </div>

      {/* Forward Valuation (前瞻估值) */}
      {forwardValuation?.length > 0 && (
        <div className="card">
          <div className="card-title">{isChinese ? '前瞻估值 · Forward Valuation' : 'Forward Valuation'}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
            {isChinese ? '基于分析师EPS预测 × 市盈率区间 (±20%)' : 'Based on analyst EPS consensus × trailing P/E range (±20%)'}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {forwardValuation.map(fv => {
              const inRange = currentPrice >= fv.low && currentPrice <= fv.high;
              const belowRange = currentPrice < fv.low;
              const rangeColor = belowRange ? '#22c55e' : inRange ? '#f59e0b' : '#ef4444';
              return (
                <div key={fv.period} style={{ flex: 1, minWidth: 200, background: 'var(--bg-surface)', borderRadius: 10, padding: '16px 20px', border: `1px solid ${rangeColor}33` }}>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}>
                    {isChinese ? `FY${fv.year} 前瞻估值` : `FY${fv.year} Forward Valuation`}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: 22, fontWeight: 700, color: rangeColor }}>
                      {fmt.currency(fv.low)} – {fmt.currency(fv.high)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                    {isChinese ? '中位' : 'Mid'} <span style={{ fontFamily: 'JetBrains Mono', fontWeight: 600, color: 'var(--text-primary)' }}>{fmt.currency(fv.mid)}</span>
                  </div>
                  {/* Visual range bar */}
                  <div style={{ position: 'relative', height: 8, background: 'var(--bg-card)', borderRadius: 4, marginBottom: 8, overflow: 'visible' }}>
                    <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '100%', background: `${rangeColor}33`, borderRadius: 4 }} />
                    <div style={{ position: 'absolute', left: '50%', top: -2, width: 2, height: 12, background: rangeColor, borderRadius: 1 }} title={`Mid: ${fmt.currency(fv.mid)}`} />
                    {currentPrice && (() => {
                      const pct = Math.min(Math.max(((currentPrice - fv.low) / (fv.high - fv.low)) * 100, 0), 100);
                      return <div style={{ position: 'absolute', left: `${pct}%`, top: -4, width: 3, height: 16, background: 'var(--accent)', borderRadius: 2, transform: 'translateX(-50%)' }} title={`Current: ${fmt.currency(currentPrice)}`} />;
                    })()}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)', marginBottom: 10 }}>
                    <span>{fmt.currency(fv.low)}</span><span>{fmt.currency(fv.high)}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    EPS Est: <span style={{ fontFamily: 'JetBrains Mono', color: 'var(--text-secondary)' }}>{fmt.currency(fv.epsEstimate)}</span>
                    &nbsp;·&nbsp; P/E: <span style={{ fontFamily: 'JetBrains Mono', color: 'var(--text-secondary)' }}>{fv.lowPE}x – {fv.highPE}x</span>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: rangeColor }}>
                    {belowRange ? '▲ Below range — potential upside' : inRange ? '◈ Price within fair range' : '▼ Above range — caution'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Price vs Fair Value Band Chart */}
      {priceHistory.length > 0 && compositeFairValue && (
        <div className="card">
          <PriceBandChart priceHistory={priceHistory} fairValue={compositeFairValue} />
        </div>
      )}

      {/* Horizontal Valuation Methods Bar */}
      {models.length > 0 && currentPrice && (
        <div className="card">
          <ValuationBars models={models} currentPrice={currentPrice} />
        </div>
      )}

      {/* DCF Assumptions */}
      <div className="card">
        <div className="card-title">DCF Assumptions</div>
        <div className="grid-3" style={{ marginBottom: 12 }}>
          {[['Discount Rate (%)', 'discountRate'], ['Terminal Growth (%)', 'terminalGrowth'], ['FCF Growth (%)', 'growthRate']].map(([label, key]) => (
            <div key={key}>
              <div className="stat-label" style={{ marginBottom: 4 }}>{label}</div>
              <input type="number" step="0.5" className="input" value={assumptions[key]}
                onChange={e => setAssumptions(a => ({ ...a, [key]: parseFloat(e.target.value) }))} />
            </div>
          ))}
        </div>
        <button className="btn btn-primary" onClick={recalc} disabled={loading}>{loading ? 'Calculating...' : 'Recalculate DCF'}</button>
      </div>

      {/* 3-Phase DCF */}
      <AdamKhooDCF
        adamKhoo={data.adamKhoo}
        currentPrice={currentPrice}
        onRecalc={recalcAdamKhoo}
        symbol={symbol}
      />

      {/* FCF History */}
      {fcfChartData.length > 0 && (
        <div className="card">
          <div className="card-title">Free Cash Flow History ($B)</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={fcfChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="year" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6 }} formatter={(v) => v != null ? [`$${v.toFixed(2)}B`, 'FCF'] : ['—', 'FCF']} />
              <Bar dataKey="FCF" radius={[4, 4, 0, 0]}>
                {fcfChartData.map((entry, i) => (
                  <Cell key={i} fill={entry.FCF >= 0 ? 'var(--accent)' : 'var(--red)'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
